/**
 * Static ISO 3166-2 subdivision data for supported countries.
 * Region codes are lowercase suffixes matching Calendarific's `location` parameter format.
 * e.g., "us-ny" → iso: "ny", "ca-qc" → iso: "qc"
 *
 * Source: ISO 3166-2 standard
 */

export interface RegionOption {
  iso: string;   // Lowercase subdivision suffix for Calendarific location param
  name: string;  // Human-readable name
}

const COUNTRY_REGIONS: Record<string, RegionOption[]> = {
  AU: [
    { iso: 'act', name: 'Australian Capital Territory' },
    { iso: 'nsw', name: 'New South Wales' },
    { iso: 'nt', name: 'Northern Territory' },
    { iso: 'qld', name: 'Queensland' },
    { iso: 'sa', name: 'South Australia' },
    { iso: 'tas', name: 'Tasmania' },
    { iso: 'vic', name: 'Victoria' },
    { iso: 'wa', name: 'Western Australia' },
  ],
  AT: [
    { iso: '1', name: 'Burgenland' },
    { iso: '2', name: 'Kärnten' },
    { iso: '3', name: 'Niederösterreich' },
    { iso: '4', name: 'Oberösterreich' },
    { iso: '5', name: 'Salzburg' },
    { iso: '6', name: 'Steiermark' },
    { iso: '7', name: 'Tirol' },
    { iso: '8', name: 'Vorarlberg' },
    { iso: '9', name: 'Wien' },
  ],
  BE: [
    { iso: 'bru', name: 'Brussels' },
    { iso: 'vlg', name: 'Flanders' },
    { iso: 'wal', name: 'Wallonia' },
  ],
  BR: [
    { iso: 'ac', name: 'Acre' },
    { iso: 'al', name: 'Alagoas' },
    { iso: 'ap', name: 'Amapá' },
    { iso: 'am', name: 'Amazonas' },
    { iso: 'ba', name: 'Bahia' },
    { iso: 'ce', name: 'Ceará' },
    { iso: 'df', name: 'Distrito Federal' },
    { iso: 'es', name: 'Espírito Santo' },
    { iso: 'go', name: 'Goiás' },
    { iso: 'ma', name: 'Maranhão' },
    { iso: 'mt', name: 'Mato Grosso' },
    { iso: 'ms', name: 'Mato Grosso do Sul' },
    { iso: 'mg', name: 'Minas Gerais' },
    { iso: 'pa', name: 'Pará' },
    { iso: 'pb', name: 'Paraíba' },
    { iso: 'pr', name: 'Paraná' },
    { iso: 'pe', name: 'Pernambuco' },
    { iso: 'pi', name: 'Piauí' },
    { iso: 'rj', name: 'Rio de Janeiro' },
    { iso: 'rn', name: 'Rio Grande do Norte' },
    { iso: 'rs', name: 'Rio Grande do Sul' },
    { iso: 'ro', name: 'Rondônia' },
    { iso: 'rr', name: 'Roraima' },
    { iso: 'sc', name: 'Santa Catarina' },
    { iso: 'sp', name: 'São Paulo' },
    { iso: 'se', name: 'Sergipe' },
    { iso: 'to', name: 'Tocantins' },
  ],
  CA: [
    { iso: 'ab', name: 'Alberta' },
    { iso: 'bc', name: 'British Columbia' },
    { iso: 'mb', name: 'Manitoba' },
    { iso: 'nb', name: 'New Brunswick' },
    { iso: 'nl', name: 'Newfoundland and Labrador' },
    { iso: 'ns', name: 'Nova Scotia' },
    { iso: 'nt', name: 'Northwest Territories' },
    { iso: 'nu', name: 'Nunavut' },
    { iso: 'on', name: 'Ontario' },
    { iso: 'pe', name: 'Prince Edward Island' },
    { iso: 'qc', name: 'Quebec' },
    { iso: 'sk', name: 'Saskatchewan' },
    { iso: 'yt', name: 'Yukon' },
  ],
  CN: [
    { iso: 'bj', name: 'Beijing' },
    { iso: 'gd', name: 'Guangdong' },
    { iso: 'sh', name: 'Shanghai' },
    { iso: 'zj', name: 'Zhejiang' },
  ],
  DE: [
    { iso: 'bw', name: 'Baden-Württemberg' },
    { iso: 'by', name: 'Bayern' },
    { iso: 'be', name: 'Berlin' },
    { iso: 'bb', name: 'Brandenburg' },
    { iso: 'hb', name: 'Bremen' },
    { iso: 'hh', name: 'Hamburg' },
    { iso: 'he', name: 'Hessen' },
    { iso: 'mv', name: 'Mecklenburg-Vorpommern' },
    { iso: 'ni', name: 'Niedersachsen' },
    { iso: 'nw', name: 'Nordrhein-Westfalen' },
    { iso: 'rp', name: 'Rheinland-Pfalz' },
    { iso: 'sl', name: 'Saarland' },
    { iso: 'sn', name: 'Sachsen' },
    { iso: 'st', name: 'Sachsen-Anhalt' },
    { iso: 'sh', name: 'Schleswig-Holstein' },
    { iso: 'th', name: 'Thüringen' },
  ],
  ES: [
    { iso: 'an', name: 'Andalucía' },
    { iso: 'ar', name: 'Aragón' },
    { iso: 'as', name: 'Asturias' },
    { iso: 'cb', name: 'Cantabria' },
    { iso: 'cl', name: 'Castilla y León' },
    { iso: 'cm', name: 'Castilla-La Mancha' },
    { iso: 'ct', name: 'Cataluña' },
    { iso: 'ce', name: 'Ceuta' },
    { iso: 'ex', name: 'Extremadura' },
    { iso: 'ga', name: 'Galicia' },
    { iso: 'ib', name: 'Islas Baleares' },
    { iso: 'cn', name: 'Islas Canarias' },
    { iso: 'ri', name: 'La Rioja' },
    { iso: 'md', name: 'Madrid' },
    { iso: 'ml', name: 'Melilla' },
    { iso: 'mc', name: 'Murcia' },
    { iso: 'nc', name: 'Navarra' },
    { iso: 'pv', name: 'País Vasco' },
    { iso: 'vc', name: 'Valencia' },
  ],
  GB: [
    { iso: 'eng', name: 'England' },
    { iso: 'nir', name: 'Northern Ireland' },
    { iso: 'sct', name: 'Scotland' },
    { iso: 'wls', name: 'Wales' },
  ],
  IN: [
    { iso: 'an', name: 'Andaman and Nicobar Islands' },
    { iso: 'ap', name: 'Andhra Pradesh' },
    { iso: 'ar', name: 'Arunachal Pradesh' },
    { iso: 'as', name: 'Assam' },
    { iso: 'br', name: 'Bihar' },
    { iso: 'ch', name: 'Chandigarh' },
    { iso: 'ct', name: 'Chhattisgarh' },
    { iso: 'dl', name: 'Delhi' },
    { iso: 'ga', name: 'Goa' },
    { iso: 'gj', name: 'Gujarat' },
    { iso: 'hr', name: 'Haryana' },
    { iso: 'hp', name: 'Himachal Pradesh' },
    { iso: 'jk', name: 'Jammu and Kashmir' },
    { iso: 'jh', name: 'Jharkhand' },
    { iso: 'ka', name: 'Karnataka' },
    { iso: 'kl', name: 'Kerala' },
    { iso: 'la', name: 'Ladakh' },
    { iso: 'mp', name: 'Madhya Pradesh' },
    { iso: 'mh', name: 'Maharashtra' },
    { iso: 'mn', name: 'Manipur' },
    { iso: 'ml', name: 'Meghalaya' },
    { iso: 'mz', name: 'Mizoram' },
    { iso: 'nl', name: 'Nagaland' },
    { iso: 'or', name: 'Odisha' },
    { iso: 'pb', name: 'Punjab' },
    { iso: 'rj', name: 'Rajasthan' },
    { iso: 'sk', name: 'Sikkim' },
    { iso: 'tn', name: 'Tamil Nadu' },
    { iso: 'tg', name: 'Telangana' },
    { iso: 'tr', name: 'Tripura' },
    { iso: 'up', name: 'Uttar Pradesh' },
    { iso: 'uk', name: 'Uttarakhand' },
    { iso: 'wb', name: 'West Bengal' },
  ],
  ID: [
    { iso: 'jk', name: 'Jakarta' },
    { iso: 'jb', name: 'Jawa Barat' },
    { iso: 'ji', name: 'Jawa Timur' },
    { iso: 'jt', name: 'Jawa Tengah' },
    { iso: 'ba', name: 'Bali' },
  ],
  JP: [
    // Japan has prefectures but Calendarific treats holidays nationally
  ],
  KR: [
    // South Korea holidays are national
  ],
  MX: [
    { iso: 'agu', name: 'Aguascalientes' },
    { iso: 'bcn', name: 'Baja California' },
    { iso: 'bcs', name: 'Baja California Sur' },
    { iso: 'cam', name: 'Campeche' },
    { iso: 'chp', name: 'Chiapas' },
    { iso: 'chh', name: 'Chihuahua' },
    { iso: 'coa', name: 'Coahuila' },
    { iso: 'col', name: 'Colima' },
    { iso: 'cmx', name: 'Ciudad de México' },
    { iso: 'dur', name: 'Durango' },
    { iso: 'gua', name: 'Guanajuato' },
    { iso: 'gro', name: 'Guerrero' },
    { iso: 'hid', name: 'Hidalgo' },
    { iso: 'jal', name: 'Jalisco' },
    { iso: 'mic', name: 'Michoacán' },
    { iso: 'mor', name: 'Morelos' },
    { iso: 'mex', name: 'México' },
    { iso: 'nay', name: 'Nayarit' },
    { iso: 'nle', name: 'Nuevo León' },
    { iso: 'oax', name: 'Oaxaca' },
    { iso: 'pue', name: 'Puebla' },
    { iso: 'que', name: 'Querétaro' },
    { iso: 'roo', name: 'Quintana Roo' },
    { iso: 'slp', name: 'San Luis Potosí' },
    { iso: 'sin', name: 'Sinaloa' },
    { iso: 'son', name: 'Sonora' },
    { iso: 'tab', name: 'Tabasco' },
    { iso: 'tam', name: 'Tamaulipas' },
    { iso: 'tla', name: 'Tlaxcala' },
    { iso: 'ver', name: 'Veracruz' },
    { iso: 'yuc', name: 'Yucatán' },
    { iso: 'zac', name: 'Zacatecas' },
  ],
  NZ: [
    { iso: 'auk', name: 'Auckland' },
    { iso: 'bop', name: 'Bay of Plenty' },
    { iso: 'can', name: 'Canterbury' },
    { iso: 'hkb', name: "Hawke's Bay" },
    { iso: 'mwt', name: 'Manawatū-Whanganui' },
    { iso: 'mbh', name: 'Marlborough' },
    { iso: 'nsn', name: 'Nelson' },
    { iso: 'ntl', name: 'Northland' },
    { iso: 'ota', name: 'Otago' },
    { iso: 'stl', name: 'Southland' },
    { iso: 'tki', name: 'Taranaki' },
    { iso: 'tas', name: 'Tasman' },
    { iso: 'wko', name: 'Waikato' },
    { iso: 'wgn', name: 'Wellington' },
    { iso: 'wtc', name: 'West Coast' },
  ],
  PH: [
    // Philippines holidays are national
  ],
  US: [
    { iso: 'al', name: 'Alabama' },
    { iso: 'ak', name: 'Alaska' },
    { iso: 'az', name: 'Arizona' },
    { iso: 'ar', name: 'Arkansas' },
    { iso: 'ca', name: 'California' },
    { iso: 'co', name: 'Colorado' },
    { iso: 'ct', name: 'Connecticut' },
    { iso: 'de', name: 'Delaware' },
    { iso: 'dc', name: 'District of Columbia' },
    { iso: 'fl', name: 'Florida' },
    { iso: 'ga', name: 'Georgia' },
    { iso: 'hi', name: 'Hawaii' },
    { iso: 'id', name: 'Idaho' },
    { iso: 'il', name: 'Illinois' },
    { iso: 'in', name: 'Indiana' },
    { iso: 'ia', name: 'Iowa' },
    { iso: 'ks', name: 'Kansas' },
    { iso: 'ky', name: 'Kentucky' },
    { iso: 'la', name: 'Louisiana' },
    { iso: 'me', name: 'Maine' },
    { iso: 'md', name: 'Maryland' },
    { iso: 'ma', name: 'Massachusetts' },
    { iso: 'mi', name: 'Michigan' },
    { iso: 'mn', name: 'Minnesota' },
    { iso: 'ms', name: 'Mississippi' },
    { iso: 'mo', name: 'Missouri' },
    { iso: 'mt', name: 'Montana' },
    { iso: 'ne', name: 'Nebraska' },
    { iso: 'nv', name: 'Nevada' },
    { iso: 'nh', name: 'New Hampshire' },
    { iso: 'nj', name: 'New Jersey' },
    { iso: 'nm', name: 'New Mexico' },
    { iso: 'ny', name: 'New York' },
    { iso: 'nc', name: 'North Carolina' },
    { iso: 'nd', name: 'North Dakota' },
    { iso: 'oh', name: 'Ohio' },
    { iso: 'ok', name: 'Oklahoma' },
    { iso: 'or', name: 'Oregon' },
    { iso: 'pa', name: 'Pennsylvania' },
    { iso: 'ri', name: 'Rhode Island' },
    { iso: 'sc', name: 'South Carolina' },
    { iso: 'sd', name: 'South Dakota' },
    { iso: 'tn', name: 'Tennessee' },
    { iso: 'tx', name: 'Texas' },
    { iso: 'ut', name: 'Utah' },
    { iso: 'vt', name: 'Vermont' },
    { iso: 'va', name: 'Virginia' },
    { iso: 'wa', name: 'Washington' },
    { iso: 'wv', name: 'West Virginia' },
    { iso: 'wi', name: 'Wisconsin' },
    { iso: 'wy', name: 'Wyoming' },
  ],
  CH: [
    { iso: 'ag', name: 'Aargau' },
    { iso: 'ai', name: 'Appenzell Innerrhoden' },
    { iso: 'ar', name: 'Appenzell Ausserrhoden' },
    { iso: 'be', name: 'Bern' },
    { iso: 'bl', name: 'Basel-Landschaft' },
    { iso: 'bs', name: 'Basel-Stadt' },
    { iso: 'fr', name: 'Fribourg' },
    { iso: 'ge', name: 'Genève' },
    { iso: 'gl', name: 'Glarus' },
    { iso: 'gr', name: 'Graubünden' },
    { iso: 'ju', name: 'Jura' },
    { iso: 'lu', name: 'Luzern' },
    { iso: 'ne', name: 'Neuchâtel' },
    { iso: 'nw', name: 'Nidwalden' },
    { iso: 'ow', name: 'Obwalden' },
    { iso: 'sg', name: 'St. Gallen' },
    { iso: 'sh', name: 'Schaffhausen' },
    { iso: 'so', name: 'Solothurn' },
    { iso: 'sz', name: 'Schwyz' },
    { iso: 'tg', name: 'Thurgau' },
    { iso: 'ti', name: 'Ticino' },
    { iso: 'ur', name: 'Uri' },
    { iso: 'vd', name: 'Vaud' },
    { iso: 'vs', name: 'Valais' },
    { iso: 'zg', name: 'Zug' },
    { iso: 'zh', name: 'Zürich' },
  ],
  SE: [
    // Sweden holidays are national
  ],
  NO: [
    // Norway holidays are national
  ],
  DK: [
    // Denmark holidays are national
  ],
  FI: [
    // Finland holidays are national
  ],
  PL: [
    // Poland holidays are national
  ],
  PT: [
    // Portugal holidays are national
  ],
  NL: [
    // Netherlands holidays are national
  ],
  IE: [
    // Ireland holidays are national
  ],
  FR: [
    { iso: 'a', name: 'Alsace' },
    { iso: 'b', name: 'Moselle' },
    // Note: Alsace-Moselle has special holidays; rest of France is national
  ],
  IT: [
    // Italy holidays are mostly national
  ],
  ZA: [
    // South Africa holidays are national
  ],
  SG: [
    // Singapore holidays are national
  ],
  HK: [
    // Hong Kong holidays are national
  ],
  MY: [
    { iso: 'jhr', name: 'Johor' },
    { iso: 'kdh', name: 'Kedah' },
    { iso: 'ktn', name: 'Kelantan' },
    { iso: 'mlk', name: 'Melaka' },
    { iso: 'nsn', name: 'Negeri Sembilan' },
    { iso: 'phg', name: 'Pahang' },
    { iso: 'prk', name: 'Perak' },
    { iso: 'pls', name: 'Perlis' },
    { iso: 'png', name: 'Pulau Pinang' },
    { iso: 'sbh', name: 'Sabah' },
    { iso: 'swk', name: 'Sarawak' },
    { iso: 'sgr', name: 'Selangor' },
    { iso: 'trg', name: 'Terengganu' },
    { iso: 'kul', name: 'Kuala Lumpur' },
    { iso: 'lbn', name: 'Labuan' },
    { iso: 'pjy', name: 'Putrajaya' },
  ],
  TH: [
    // Thailand holidays are national
  ],
  VN: [
    // Vietnam holidays are national
  ],
};

/**
 * Get regions for a given country code.
 * Returns an empty array for countries without subdivision-specific holidays.
 */
export function getRegionsForCountry(countryCode: string): RegionOption[] {
  return COUNTRY_REGIONS[countryCode.toUpperCase()] ?? [];
}
